import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import moment from 'moment';
import { useMemo } from 'react';
import TicketLinkImport, {
    TicketLinkImportStatus,
} from 'src/data/models/ticket-link-import/TicketLinkImport';
import { dateFormat } from 'src/shared/date';
import { StatusChip, StatusChipVariantType } from 'src/view/components/status-label/StatusChip';
import Table from 'src/view/components/table/table/Table';
import {
    NewTableColumn,
    TABLE_CELL_WIDTH,
    TablePaginationData,
} from 'src/view/components/table/table/Types';

function getStatusVariant(status: TicketLinkImportStatus): StatusChipVariantType {
    switch (status) {
        case 'Applied':
        case 'Applying':
            return 'green';
        case 'Matched':
            return 'blue';
        case 'Failed':
        case 'PartiallyMatched':
        case 'PartiallyApplied':
            return 'red';
        case 'Pending':
        default:
            return 'grey';
    }
}

export interface TicketLinkImportTableProps {
    ticketLinkImports: TicketLinkImport[];
    loading: boolean;
    pagination: TablePaginationData;
    onClickDetails: (importId: string) => void;
}

export default function TicketLinkImportTable(
    props: Readonly<TicketLinkImportTableProps>
): JSX.Element {
    const columns = useMemo<NewTableColumn<TicketLinkImport>[]>(
        () => [
            {
                key: 'createdAt',
                title: 'Creation date',
                cellRenderer: (linkImport) => (
                    <span>{moment(linkImport.createdAt).local().format(dateFormat)}</span>
                ),
                width: TABLE_CELL_WIDTH.MEDIUM,
            },
            {
                key: 'ticketCount',
                title: 'Amount of tickets',
                cellRenderer: (linkImport) => <span>{linkImport.ticketCount}</span>,
                width: TABLE_CELL_WIDTH.SMALL,
            },
            {
                key: 'status',
                title: 'Import Status',
                cellRenderer: (linkImport) => (
                    <StatusChip
                        variant={getStatusVariant(linkImport.status)}
                        text={linkImport.status}
                    />
                ),
                width: TABLE_CELL_WIDTH.MEDIUM,
            },
            {
                align: 'right',
                cellRenderer: () => <VisibilityOutlinedIcon />,
            },
        ],
        []
    );

    return (
        <Table<TicketLinkImport, TicketLinkImport>
            data={props.ticketLinkImports}
            rowIdResolver={(rowData: TicketLinkImport) => ({
                id: rowData.id,
                data: { ...rowData },
            })}
            onClickRow={(rowData: TicketLinkImport) => props.onClickDetails(rowData.id)}
            columns={columns}
            loading={props.loading}
            pagination={props.pagination}
            minHeight={425}
        />
    );
}
